import { ThemeEvents } from '@theme/events';

/**
 * Keeps the subscription benefit lines in sync with the selected variant.
 *
 * Selling plans are per-variant, not per-product: on this store the 40-load variants carry no
 * allocations while 100/200 do. The block's Liquid already gates on the selected variant, but a PDP
 * variant change only morphs <variant-picker> — every other block is expected to update itself from
 * the freshly rendered document carried on `variant:update`. Without this component the benefit lines
 * keep whatever the page first rendered with, so they advertise a subscription on variants that
 * can't be subscribed to.
 *
 * The custom element is always rendered (empty when there's no offer) so it survives as a listener
 * and can fill back in when the shopper returns to a subscribable variant.
 */
class SubscriptionBenefits extends HTMLElement {
  /** @type {EventTarget | null} */
  #target = null;

  connectedCallback() {
    this.#target = this.closest('.shopify-section, dialog') || document;
    this.#target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.#target?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#target = null;
  }

  #onVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    // Same product guard as product-price.js: ignore updates fired for a different product
    // (combined listings swap the product out from under us).
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const next = html.querySelector(`subscription-benefits[data-block-id="${this.dataset.blockId}"]`);
    if (!next) return;

    // Empty when the newly selected variant has no subscription offer, which is the point.
    this.replaceChildren(...next.cloneNode(true).childNodes);
  };
}

if (!customElements.get('subscription-benefits')) {
  customElements.define('subscription-benefits', SubscriptionBenefits);
}
